import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Heart,
  Sparkles,
  Star,
  Send,
  Mail,
  Shield,
  Check,
  ArrowUpRight,
  Crown,
  Music,
  Bell,
  Gift,
  Settings,
  Mic,
  Type,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import SectionLabel from "@/components/SectionLabel";
import ShareButtons from "@/components/ShareButtons";
import VoiceRecorder, { voiceRecordingIsReady } from "@/components/VoiceRecorder";
import { HERO_NINA as HERO_IMG } from "@/lib/assets";

const BENEFITS = [
  {
    icon: Bell,
    title: "Priority Updates",
    copy: "First word on every release, tour announcement, and Inner Circle event — straight to your inbox.",
  },
  {
    icon: Sparkles,
    title: "Behind-The-Scenes",
    copy: "Studio session photos, in-progress lyrics, and unreleased visuals you won't see anywhere else.",
  },
  {
    icon: Music,
    title: "Early Music Previews",
    copy: "Hear Audio Porn cuts and OVA 40 deluxe edits weeks before they hit Spotify.",
  },
  {
    icon: Heart,
    title: "Special Artist Messages",
    copy: "Personal voice notes and video drops from Nina — yes, she actually records them.",
  },
  {
    icon: Gift,
    title: "Monthly Fan Appreciation",
    copy: "Each month one member is chosen to receive a thank-you gift directly from Nina.",
  },
];

export default function FanClub() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status");
  const sessionId = params.get("session_id");

  // Track payment status for return-from-Stripe flow
  useEffect(() => {
    if (status === "success" && sessionId) {
      toast.loading("Confirming your membership…", { id: "fc-poll" });
      let attempts = 0;
      const maxAttempts = 6;
      const poll = async () => {
        attempts += 1;
        try {
          const { data } = await api.get(
            `/public/fan-club/checkout/status/${sessionId}`
          );
          if (data.payment_status === "paid") {
            toast.success(
              "Welcome to the Inner Circle. You're officially in. 💛",
              { id: "fc-poll" }
            );
            // Clean URL
            const np = new URLSearchParams(params);
            np.delete("status");
            np.delete("session_id");
            setParams(np, { replace: true });
            return;
          }
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000);
          } else {
            toast.dismiss("fc-poll");
            toast.message(
              "Still finalizing — check your email shortly for confirmation.",
              { id: "fc-poll" }
            );
          }
        } catch {
          if (attempts < maxAttempts) setTimeout(poll, 2000);
          else toast.dismiss("fc-poll");
        }
      };
      poll();
    } else if (status === "cancelled") {
      toast("Checkout cancelled. No charge.", { icon: "ℹ️" });
      const np = new URLSearchParams(params);
      np.delete("status");
      setParams(np, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, sessionId]);

  return (
    <div data-testid="fan-club-page">
      {/* HERO */}
      <section className="relative min-h-[62vh] flex items-end overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Nina Capone Inner Circle Fan Club"
          className="absolute inset-0 w-full h-full object-cover object-[60%_30%] opacity-55"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,3,8,0.4) 0%, rgba(5,3,8,0.95) 95%)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-24 w-full">
          <div className="max-w-3xl space-y-6 fade-up">
            <SectionLabel number="05" label="Inner Circle" />
            <h1 className="font-serif font-light tracking-tighter text-[2.6rem] sm:text-6xl lg:text-8xl leading-[0.95]">
              The Fan{" "}
              <em className="italic" style={{ color: "var(--gold)" }}>
                Club.
              </em>
            </h1>
            <p className="text-white/75 text-base sm:text-lg max-w-xl leading-relaxed">
              Two ways to ride with Nina. Both are real seats at the table —
              one is free, one comes with extra access. Pick what feels right.
            </p>
            <div className="flex items-center gap-3 pt-1" data-testid="fan-club-share">
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">Share</span>
              <ShareButtons
                shareUrl={`${window.location.origin}/api/og/page/fan-club`}
                canonicalUrl={`${window.location.origin}/fan-club`}
                text="Join Nina Capone's Fan Club — The Inner Circle"
                testidPrefix="fan-club-share"
              />
            </div>
          </div>
        </div>
      </section>

      {/* TIERS */}
      <section className="py-20 border-t border-white/5" data-testid="fan-tiers">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-6 lg:gap-8">
          <FreeTier />
          <PaidTier />
        </div>
        <p
          className="max-w-2xl mx-auto text-center text-[11px] uppercase tracking-[0.22em] text-white/35 mt-10 px-6 leading-relaxed"
          data-testid="fan-club-disclaimer"
        >
          Both tiers receive priority updates and Inner Circle dispatches.
          The paid tier is entirely optional — you can join for free and still
          be considered for monthly fan appreciation. Monthly featured
          supporters are selected based on positive engagement and community
          support. Selection is determined by the artist/admin.
        </p>
      </section>

      {/* BENEFITS */}
      <section className="py-20 border-t border-white/5" data-testid="fan-benefits">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-14 space-y-3">
            <SectionLabel label="Benefits" />
            <h2 className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              What you get when you{" "}
              <em className="italic" style={{ color: "var(--gold)" }}>
                ride with us.
              </em>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {BENEFITS.map((b) => (
              <div
                key={b.title}
                className="surface p-7 lg:p-8 space-y-3"
                data-testid={`benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <b.icon size={20} style={{ color: "var(--gold)" }} />
                <h3 className="font-serif text-2xl tracking-tighter font-light leading-tight">
                  {b.title}
                </h3>
                <p className="text-sm text-white/55 leading-relaxed">{b.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAN MAIL */}
      <FanMailSection />

      {/* MANAGE EXISTING MEMBERSHIP */}
      <ManageSection />
    </div>
  );
}

function FreeTier() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/public/subscribe", {
        email,
        source: "fan-club-free",
      });
      if (data.already_subscribed && !data.pending_confirmation) {
        toast.success("You're already in. We see you.");
      } else {
        toast.success("Check your inbox — confirm your email to lock in.");
      }
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't sign up. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface p-8 lg:p-10 flex flex-col" data-testid="tier-free">
      <div className="flex items-center justify-between mb-6">
        <span className="eyebrow">Free Tier · Inner Circle</span>
        <Heart size={20} style={{ color: "var(--gold)" }} />
      </div>
      <h3 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light leading-[0.95] mb-3">
        Join Free.
      </h3>
      <p className="font-serif italic text-white/55 text-lg mb-6">
        For everyone. Always.
      </p>
      <ul className="space-y-3 mb-8 text-sm text-white/70">
        <Feat>Priority updates from Nina</Feat>
        <Feat>Weekly Wellness Blog dispatches</Feat>
        <Feat>Event + show announcements first</Feat>
        <Feat>Monthly fan appreciation eligibility</Feat>
      </ul>
      {done ? (
        <div className="mt-auto surface p-5 text-center" style={{ background: "rgba(232,180,184,0.06)" }}>
          <Mail size={22} style={{ color: "var(--gold)" }} className="mx-auto mb-2" />
          <p className="text-sm text-white/75">
            Check your inbox — confirm your email to lock your free spot.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-auto space-y-3" data-testid="fan-free-form">
          <Label htmlFor="fan-free-email" className="eyebrow">
            Your Email
          </Label>
          <div className="flex gap-2">
            <Input
              id="fan-free-email"
              type="email"
              required
              placeholder="you@yourdomain.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 flex-1 h-11"
              data-testid="fan-free-email-input"
            />
            <Button
              type="submit"
              disabled={busy}
              className="btn-ghost-gold rounded-none px-5 h-11 text-xs whitespace-nowrap"
              data-testid="fan-free-submit"
            >
              {busy ? "…" : "Join Free"}
            </Button>
          </div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-white/35">
            $0 forever · Double opt-in
          </p>
        </form>
      )}
    </div>
  );
}

function PaidTier() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState({ price_usd: 20, automatic_tax: false });

  useEffect(() => {
    api
      .get("/public/fan-club/settings")
      .then((r) => setSettings(r.data || { price_usd: 20, automatic_tax: false }))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/public/fan-club/checkout", {
        email,
        name: name || undefined,
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      toast.error("Couldn't start checkout. Please try again.");
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) ||
          "Payments aren't enabled yet — please try again shortly."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="surface p-8 lg:p-10 flex flex-col relative overflow-hidden ring-1 ring-[color:var(--gold)]/40"
      data-testid="tier-paid"
    >
      <div
        className="absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-20 blur-3xl pointer-events-none"
        style={{ background: "var(--gold)" }}
      />
      <div className="relative flex items-center justify-between mb-6">
        <span
          className="inline-flex items-center gap-2 px-2.5 py-1 text-[10px] uppercase tracking-[0.3em] border"
          style={{ color: "var(--gold)", borderColor: "rgba(232,180,184,0.5)" }}
        >
          <Crown size={11} /> Optional · Paid Tier
        </span>
        <Star size={20} style={{ color: "var(--gold)" }} fill="var(--gold)" fillOpacity={0.3} />
      </div>
      <h3 className="relative font-serif text-4xl lg:text-5xl tracking-tighter font-light leading-[0.95] mb-3">
        <span style={{ color: "var(--gold)" }}>${settings.price_usd}</span>
        <span className="text-2xl text-white/55 ml-2">
          /month{settings.automatic_tax ? <span className="text-sm"> + tax</span> : null}
        </span>
      </h3>
      <p className="relative font-serif italic text-white/55 text-lg mb-6">
        Inner Circle · Paid Membership
      </p>
      <ul className="relative space-y-3 mb-8 text-sm text-white/75">
        <Feat>Everything in the free tier</Feat>
        <Feat highlight>Behind-the-scenes drops + studio diaries</Feat>
        <Feat highlight>Early music previews (Audio Porn cuts)</Feat>
        <Feat highlight>Special voice & video notes from Nina</Feat>
        <Feat highlight>Higher engagement → higher chance of monthly featured supporter</Feat>
      </ul>
      <form onSubmit={submit} className="relative mt-auto space-y-3" data-testid="fan-paid-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="fan-paid-name" className="eyebrow">
              Your Name
            </Label>
            <Input
              id="fan-paid-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 h-11"
              data-testid="fan-paid-name-input"
              placeholder="Optional"
            />
          </div>
          <div>
            <Label htmlFor="fan-paid-email" className="eyebrow">
              Email
            </Label>
            <Input
              id="fan-paid-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 h-11"
              data-testid="fan-paid-email-input"
              placeholder="you@yourdomain.com"
            />
          </div>
        </div>
        <Button
          type="submit"
          disabled={busy}
          className="btn-gold rounded-none w-full h-12 text-xs inline-flex items-center justify-center gap-2"
          data-testid="fan-paid-submit"
        >
          {busy
            ? "Redirecting to Stripe…"
            : `Become a Member · $${settings.price_usd} / mo${settings.automatic_tax ? " + tax" : ""}`}
          <ArrowUpRight size={14} />
        </Button>
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-white/40">
          <span className="inline-flex items-center gap-1.5">
            <Shield size={11} /> Stripe-secured
          </span>
          <span>Cancel anytime · Manage via portal</span>
        </div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-white/40 leading-relaxed pt-2 border-t border-white/5">
          By subscribing you agree to our{" "}
          <Link to="/terms" className="text-[color:var(--gold)] hover:underline" data-testid="fan-paid-terms-link">Terms</Link>
          ,{" "}
          <Link to="/privacy" className="text-[color:var(--gold)] hover:underline" data-testid="fan-paid-privacy-link">Privacy</Link>
          , and{" "}
          <Link to="/refund" className="text-[color:var(--gold)] hover:underline" data-testid="fan-paid-refund-link">Refund Policy</Link>.
        </p>
      </form>
    </div>
  );
}

function Feat({ children, highlight }) {
  return (
    <li className="flex items-start gap-2.5">
      <Check
        size={14}
        className="mt-0.5 shrink-0"
        style={{ color: highlight ? "var(--gold)" : "rgba(232,180,184,0.7)" }}
      />
      <span className={highlight ? "text-white/85" : "text-white/65"}>{children}</span>
    </li>
  );
}

function FanMailSection() {
  const [mode, setMode] = useState("text"); // 'text' | 'voice'
  const [form, setForm] = useState({ name: "", email: "", message: "", photo_url: "" });
  const [voiceRec, setVoiceRec] = useState(null); // { blob, mime, duration } | null
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const resetAll = () => {
    setDone(false);
    setForm({ name: "", email: "", message: "", photo_url: "" });
    setVoiceRec(null);
    setMode("text");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Drop your email so Nina can write back.");
      return;
    }
    if (mode === "text" && form.message.trim().length < 10) {
      toast.error("A few more words? At least 10 characters.");
      return;
    }
    if (mode === "voice" && !voiceRecordingIsReady(voiceRec)) {
      toast.error("Record a voice note first.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "voice") {
        const fd = new FormData();
        fd.append("email", form.email);
        if (form.name) fd.append("name", form.name);
        if (voiceRec.duration) fd.append("duration_sec", String(voiceRec.duration));
        if (form.message && form.message.trim().length >= 1) {
          fd.append("transcript", form.message.trim().slice(0, 500));
        }
        const ext = (voiceRec.mime || "audio/webm").includes("mp4")
          ? "m4a"
          : (voiceRec.mime || "audio/webm").includes("ogg")
            ? "ogg"
            : "webm";
        fd.append("file", voiceRec.blob, `voice-note.${ext}`);
        await api.post("/public/fan-mail/voice", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        const payload = {
          email: form.email,
          message: form.message,
          ...(form.name ? { name: form.name } : {}),
          ...(form.photo_url ? { photo_url: form.photo_url } : {}),
        };
        await api.post("/public/fan-mail", payload);
      }
      setDone(true);
      toast.success("Nina got it. Thank you.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't send. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="fan-mail"
      className="relative py-24 border-t border-white/5 overflow-hidden scroll-mt-24"
      data-testid="fan-mail-section"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          background:
            "radial-gradient(ellipse 60% 80% at 0% 0%, rgba(232,180,184,0.08), transparent 60%), radial-gradient(ellipse 70% 80% at 100% 100%, rgba(74,14,62,0.25), transparent 60%)",
        }}
      />
      <div className="relative max-w-[1100px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 space-y-5">
          <SectionLabel label="Send Fan Mail" />
          <h2 className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            Drop Nina a{" "}
            <em className="italic" style={{ color: "var(--gold)" }}>
              note.
            </em>
          </h2>
          <p className="text-white/65 leading-relaxed">
            Type a letter or leave a 60-second voice note. Tell her what a
            record means to you. Tell her you finally booked that show. She
            reads (and listens to) them all — and her team uses fan-mail
            engagement when picking each month's featured supporter.
          </p>
          <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 flex items-center gap-2">
            <Shield size={12} style={{ color: "var(--gold)" }} />
            Private by default · Never publicly displayed
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div className="surface p-10 lg:p-12 text-center space-y-5" data-testid="fan-mail-success">
              <Heart size={36} style={{ color: "var(--gold)" }} fill="var(--gold)" fillOpacity={0.2} className="mx-auto" />
              <h3 className="font-serif text-3xl tracking-tighter font-light">
                Letter received.{" "}
                <em className="italic" style={{ color: "var(--gold)" }}>
                  Thank you.
                </em>
              </h3>
              <p className="text-white/65 text-sm max-w-md mx-auto leading-relaxed">
                Your engagement counts toward this month's featured supporter
                drawing. Share another note any time.
              </p>
              <button
                type="button"
                onClick={resetAll}
                className="text-xs uppercase tracking-[0.25em] text-white/55 hover:text-[color:var(--gold)] transition"
                data-testid="fan-mail-reset"
              >
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="surface p-7 lg:p-10 space-y-5" data-testid="fan-mail-form">
              {/* Mode toggle */}
              <div
                className="inline-flex items-center gap-1 p-1 border border-white/10 rounded-full text-[10px] uppercase tracking-[0.22em]"
                data-testid="fan-mail-mode-toggle"
                role="tablist"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "text"}
                  onClick={() => setMode("text")}
                  className={`inline-flex items-center gap-1.5 h-8 px-4 rounded-full transition ${
                    mode === "text"
                      ? "bg-[color:var(--gold)]/15 text-[color:var(--gold)]"
                      : "text-white/55 hover:text-white"
                  }`}
                  data-testid="fan-mail-mode-text"
                >
                  <Type size={11} />
                  Write
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "voice"}
                  onClick={() => setMode("voice")}
                  className={`inline-flex items-center gap-1.5 h-8 px-4 rounded-full transition ${
                    mode === "voice"
                      ? "bg-[color:var(--gold)]/15 text-[color:var(--gold)]"
                      : "text-white/55 hover:text-white"
                  }`}
                  data-testid="fan-mail-mode-voice"
                >
                  <Mic size={11} />
                  Voice
                </button>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fm-name" className="eyebrow">Your Name (optional)</Label>
                  <Input
                    id="fm-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 h-11"
                    data-testid="fm-name"
                  />
                </div>
                <div>
                  <Label htmlFor="fm-email" className="eyebrow">Email</Label>
                  <Input
                    id="fm-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 h-11"
                    data-testid="fm-email"
                  />
                </div>
              </div>

              {mode === "text" ? (
                <>
                  <div>
                    <Label htmlFor="fm-message" className="eyebrow">Your Message</Label>
                    <Textarea
                      id="fm-message"
                      required
                      rows={6}
                      minLength={10}
                      maxLength={4000}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="bg-transparent border border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] resize-none"
                      data-testid="fm-message"
                      placeholder="Tell Nina what's on your mind…"
                    />
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/35 text-right mt-1">
                      {form.message.length} / 4000
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="fm-photo" className="eyebrow">Photo URL (optional)</Label>
                    <Input
                      id="fm-photo"
                      type="url"
                      value={form.photo_url}
                      onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
                      className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 h-11"
                      data-testid="fm-photo"
                      placeholder="https://..."
                    />
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <VoiceRecorder
                    onChange={setVoiceRec}
                    disabled={busy}
                  />
                  <div>
                    <Label htmlFor="fm-transcript" className="eyebrow">
                      Quick caption (optional, helps Nina's inbox)
                    </Label>
                    <Input
                      id="fm-transcript"
                      maxLength={500}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 h-11"
                      data-testid="fm-transcript"
                      placeholder="e.g. Track 7 saved me through grad school"
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-white/5 gap-3 flex-wrap">
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">
                  {mode === "voice"
                    ? "Voice notes earn +5 bonus engagement"
                    : "Engagement counts toward monthly drawing"}
                </p>
                <Button
                  type="submit"
                  disabled={
                    busy ||
                    (mode === "text" && form.message.trim().length < 10) ||
                    (mode === "voice" && !voiceRecordingIsReady(voiceRec))
                  }
                  className="btn-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
                  data-testid={mode === "voice" ? "fan-mail-voice-submit" : "fm-submit"}
                >
                  {busy ? "Sending…" : mode === "voice" ? "Send Voice Note" : "Send Fan Mail"}
                  <Send size={14} />
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function ManageSection() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const openPortal = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/public/fan-club/portal", {
        email,
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
        return;
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        toast.error("No paid membership found for that email.");
      } else {
        toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't open portal.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="manage"
      className="py-20 border-t border-white/5 scroll-mt-24"
      data-testid="manage-section"
    >
      <div className="max-w-[900px] mx-auto px-6 lg:px-12 surface p-10 lg:p-14">
        <div className="flex items-start gap-4 mb-5">
          <Settings size={22} style={{ color: "var(--gold)" }} className="mt-1" />
          <div>
            <p className="eyebrow mb-2">Already a Paid Member?</p>
            <h3 className="font-serif text-3xl lg:text-4xl tracking-tighter font-light leading-tight mb-2">
              Manage your{" "}
              <em className="italic" style={{ color: "var(--gold)" }}>
                membership.
              </em>
            </h3>
            <p className="text-white/65 text-sm leading-relaxed max-w-xl">
              Update your card, change billing details, or cancel — all
              through Stripe's secure customer portal. You'll get a one-tap
              link emailed to you.
            </p>
          </div>
        </div>
        <form onSubmit={openPortal} className="flex flex-col sm:flex-row gap-3 mt-4" data-testid="manage-form">
          <Input
            type="email"
            required
            placeholder="The email on your subscription"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 flex-1 h-12"
            data-testid="manage-email-input"
          />
          <Button
            type="submit"
            disabled={busy}
            className="btn-ghost-gold rounded-none px-7 h-12 text-xs whitespace-nowrap"
            data-testid="manage-portal-submit"
          >
            {busy ? "Opening…" : "Open Customer Portal"}
            <ArrowUpRight size={14} className="ml-2" />
          </Button>
        </form>
        <p className="text-[10px] uppercase tracking-[0.22em] text-white/35 mt-4">
          Powered by Stripe · Free tier members don't need this
        </p>
      </div>
    </section>
  );
}
